import _ from 'lodash';

import {type RunsDataQueryConfig} from '../../../containers/RunsDataLoader';
import {RETRY_AFTER_SECONDS_EXTENSION} from '../../../util/apolloRetryAfter';
import {type RequestCause} from '../../../util/apolloRetryHeader';
import {recordRumDurationVital} from '../../../util/datadog/datadogRum';
import {MULTI_METRIC_MAX_RUNS} from '../defaults';
import {type BucketedLine} from '../types';
import {type SingleQuery} from './../../../util/queryTypes';
import {columnarBucketedQuery} from './columnar/fetchHistoryColumnar';
import {
  BackendLinesIdQueryBehavior,
  BackendLinesQueryBehavior,
  BucketedQueryBehavior,
  BucketedQueryWithInternalIdBehavior,
  GroupedBackendLinesQueryBehavior,
  GroupedBucketedQueryBehavior,
  GroupedBucketedQueryWithInternalIdBehavior,
} from './queries/behaviors';
import {
  type BucketedQueryType,
  type QueryVars,
  queryVars,
} from './queries/queryVars';
import {
  type AnyDeltaQueryResult,
  type BaseQueryBehavior,
} from './queries/types';
import {getLineGroup} from './queries/utils';
import {type LoggingInfo} from './useLoggingInfo';

/**
 * Extracts the `incomplete` array from any delta query result.
 * All query results have: data.project.{runs|groupedRuns}.deltas.incomplete
 */
function extractIncomplete(result: AnyDeltaQueryResult): string[] {
  const project = result?.data?.project;
  if (!project) {
    return [];
  }

  // Check for runs (standard queries)
  if ('runs' in project) {
    return project.runs?.deltas?.incomplete ?? [];
  }

  // Check for groupedRuns (grouped queries)
  if ('groupedRuns' in project) {
    return project.groupedRuns?.deltas?.incomplete ?? [];
  }

  return [];
}

/**
 * Retry-After (seconds) for partial shed on HTTP 200.
 *
 * Apollo Client 3's `client.query()` drops FetchResult.extensions, so delta
 * execution passes a context holder that retryAfterExtensionsLink fills; the
 * execution helper then reattaches `extensions.retryAfterSeconds` on the
 * returned result. Read that here — do not expect a bare client.query() result
 * to carry extensions.
 */
function extractRetryAfterSeconds(
  result: AnyDeltaQueryResult & {extensions?: Record<string, unknown>}
): number | undefined {
  const value = result.extensions?.[RETRY_AFTER_SECONDS_EXTENSION];
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

async function executeQueryBehavior<
  TRes extends AnyDeltaQueryResult,
  TRuns extends {__typename: string},
>(
  behavior: BaseQueryBehavior<TRes, TRuns>,
  vars: QueryVars,
  runIds: string[],
  lastUpdated: string,
  currentRunsById: Record<string, BucketedLine>,
  cause?: RequestCause
) {
  const oldLimit = vars.limit;
  let newLimit = oldLimit;

  if (vars.bucketedHistorySpecs.length > 1) {
    newLimit = Math.max(
      1,
      Math.floor(MULTI_METRIC_MAX_RUNS / vars.bucketedHistorySpecs.length)
    );
    // don't let multi-metrics ever query for more runs than the single metric limit
    vars.limit = Math.min(newLimit, oldLimit);
  }

  const absoluteStartTime = Date.now();
  const t0 = performance.now();
  let result = await behavior.query(vars, runIds, lastUpdated, cause);
  const t1 = performance.now();

  // Capture before applyPinnedSubQuery — immer produce may not preserve a
  // non-typed extensions bag on the result.
  const retryAfterSeconds = extractRetryAfterSeconds(result);

  // Must merge pinned sub-query into the main result BEFORE any downstream processing
  if (behavior.applyPinnedSubQuery) {
    result = behavior.applyPinnedSubQuery(result);
  }

  // Extract incomplete run IDs that need follow-up queries
  const incomplete = extractIncomplete(result);

  const runs = behavior.unpack(result);

  if (behavior.postProcess) {
    behavior.postProcess(runs);
  }
  const {runsById, order} = behavior.applyDeltaFn({
    currentRunsById,
    runs,
    specs: vars.bucketedHistorySpecs,
  });

  // Calculate run counts accounting for baseline, pinned, and incomplete runs
  const incompleteSet = new Set(incomplete);
  const loadedRunCount = Object.keys(runsById ?? {}).filter(
    id => !incompleteSet.has(id)
  ).length;
  const numMetrics = vars.bucketedHistorySpecs.length;

  const logData = {
    behavior: behavior.queryName,
    bins: vars.bins,
    isInitialQuery: lastUpdated === new Date(0).toISOString(),
    isMultiMetrics: vars.bucketedHistorySpecs.length > 1,
    isSystemMetrics: vars.isSystemMetrics,
    isThrottledMultiMetrics: false,
    lastUpdated,
    groupKeys: vars.groupKeys,
    hasIncomplete: incomplete.length > 0,
    incompleteCount: incomplete.length,
    internalId: vars.internalId,
    maxGroups: vars.maxGroups,
    newLimit,
    newLoad: newLimit * numMetrics,
    numMetrics,
    loadedRunCount,
    numSpecs: vars.bucketedHistorySpecs.length,
    oldLoad: oldLimit * numMetrics,
    oldLimit,
    order: vars.order,
    panelType: vars.panelType,
    project: vars.projectName,
    totalLines: loadedRunCount * numMetrics,
    lineGroup: getLineGroup(loadedRunCount * numMetrics),
  };

  /**
   * APM traces in Datadog are hard to segment when the query args are JSON strings (like `bucketedHistorySpecs`). For analysis it's easier to compute certain kinds of information in the client and track it through a RUM action.
   *
   * Here we're primarily interested in tracking frequency and performance of multi-metric panels which is hard to slice in Datadog.
   */
  recordRumDurationVital('LinePlotQueryProfile', {
    description: 'Measures the performance of the line plot query',
    context: logData,
    startTime: absoluteStartTime,
    duration: t1 - t0,
  });

  return {
    incomplete,
    retryAfterSeconds,
    isAggregated: runs?.__typename === 'GroupedRunConnection',
    /**
     * Note: `order` may contain IDs not present in `runsById` during progressive loading.
     * This preserves the server's intended display order while allowing partial rendering.
     * Consumers iterating over `order` should handle missing entries in `runsById`.
     */
    order,
    /**
     * Filter `runsById` to what should actually render this tick:
     *  - Skip incomplete runs. The server marks a run as `incomplete` when it
     *    hasn't finished populating its data; `fetchIncompleteRuns()` will fill
     *    it in via a follow-up query. Rendering it now would show an empty
     *    placeholder line.
     *  - Skip IDs whose `runsById[id]` is null/undefined. `applyDelta` builds
     *    `runsById` keyed by `order`, and per the `order` comment above, the
     *    server may list an ID in `order` before any data exists for it — that
     *    leaves a missing entry here that consumers shouldn't see.
     */
    runsById: Object.keys(runsById).reduce(
      (acc, id) => {
        if (incompleteSet.has(id) || runsById[id] == null) {
          return acc;
        }
        acc[id] = runsById[id];
        return acc;
      },
      {} as Record<string, BucketedLine>
    ),
  };
}

export async function bucketedQuery({
  currentRunsById,
  expressions,
  nBuckets,
  queryType,
  runsDataQuery,
  singleQuery,
  loggingInfo,
  timeout,
  cause,
}: {
  currentRunsById: Record<string, BucketedLine>;
  expressions: string[] | undefined;
  nBuckets: number;
  queryType: BucketedQueryType;
  runsDataQuery: RunsDataQueryConfig;
  singleQuery: SingleQuery;
  loggingInfo: LoggingInfo;
  timeout: number | undefined;
  cause?: RequestCause;
}) {
  const runIds = Object.keys(currentRunsById);
  const lastUpdated =
    _.max(Object.values(currentRunsById).map(l => l.updatedAt)) ??
    new Date(0).toISOString();
  const vars = queryVars(
    runsDataQuery,
    singleQuery,
    nBuckets,
    queryType,
    expressions,
    'Run History Line Plot',
    timeout
  );

  // This is a switch statement because Typescript is too dumb to infer the type of the queryBehavior properly, even though
  // everything else is the same.
  // You'd think that this would work:
  //
  // const queryBehaviorsByType = {
  //   BucketedQuery: BucketedQueryBehavior,
  //   BucketedQueryWithInternalId: BucketedQueryWithInternalIdBehavior,
  //   GroupedBucketedQuery: GroupedBucketedQueryBehavior,
  //   GroupedBucketedQueryWithInternalId: GroupedBucketedQueryWithInternalIdBehavior,
  // }
  // const queryBehavior = queryBehaviorsByType[queryType];
  // return await executeQueryBehavior(queryBehavior, vars, runIds, lastUpdated, currentRunsById);
  //
  // But it doesn't, even though it's functionally equivalent.
  switch (queryType) {
    case 'BucketedBackendLinesQuery':
      return await executeQueryBehavior(
        BackendLinesQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById,
        cause
      );
    case 'BucketedBackendLinesQueryId':
      return await executeQueryBehavior(
        BackendLinesIdQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById,
        cause
      );
    case 'BucketedQuery':
      return await executeQueryBehavior(
        BucketedQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById,
        cause
      );
    case 'BucketedQueryId':
      return await executeQueryBehavior(
        BucketedQueryWithInternalIdBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById,
        cause
      );
    case 'BucketedColumnarQuery':
      return await columnarBucketedQuery(vars);
    case 'GroupedBucketedQuery':
      return await executeQueryBehavior(
        GroupedBucketedQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById,
        cause
      );
    case 'GroupedBucketedQueryId':
      return await executeQueryBehavior(
        GroupedBucketedQueryWithInternalIdBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById,
        cause
      );
    case 'GroupedBucketedBackendLinesQuery':
      return await executeQueryBehavior(
        GroupedBackendLinesQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        currentRunsById,
        cause
      );
  }
}
